// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportPartFieldPropsSchemaChart.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import { CHART_TYPE } from 'logic/data/Izenda.ChartDefaultConfig';
import commonFieldPropsSchema from 'modules/ReportDesigner/Fields/PropertiesBuilder/Izenda.ReportPartFieldPropsSchema';
import { ContainerName } from 'logic/data/Izenda.ReportPartChartConstants';
import AuthorizationConstants from 'logic/data/Izenda.AuthorizationConstants';
import PropertiesEditorUtils from 'share/utils/Izenda.PropertiesEditorUtils';
import { remove, get } from 'lodash/fp';

const UNUSED_PROPS = [
  'fields.dataFormattings.fields.fontFormat',
  'fields.dataFormattings.fields.fontStyle',
  'fields.dataFormattings.fields.alignment',
  'fields.dataFormattings.fields.subTotal',
  'fields.dataFormattings.fields.grandTotal',
  'fields.dataFormattings.fields.width',
  'fields.dataFormattings.fields.columnWordWrap',
  'fields.dataFormattings.fields.verticalAlignment',
  'fields.headerFormating'
];

export default function(editorComp, reportDesignerModel, fieldStore, itemProps) {
  const PERMISSION_OPERATOR = itemProps.authorizationConstants.PERMISSION_OPERATOR;
  const PERMISSION_KEY = itemProps.authorizationConstants.PERMISSION_KEY;
  const authorization = itemProps.authorization;

  const configField = itemProps.configField || {};
  const configReportPart = reportDesignerModel.configReportPart;
  const reportPartContent = configReportPart.reportPartContent;
  const fieldContainerDetails = itemProps.fieldContainerDetails || {};

  let chartFieldPropsSchema = commonFieldPropsSchema.apply(null, arguments);
  const containerName = fieldContainerDetails.name || '';

  const mappingSource = chartFieldPropsSchema.mappingSource;
  let mappingProps = chartFieldPropsSchema.mappingProps;
  mappingProps.stypeOptions = remove(
    {
      value: 'embedded'
    },
    mappingProps.stypeOptions
  );

  // update schema
  PropertiesEditorUtils.removeProps(chartFieldPropsSchema, UNUSED_PROPS);

  // show cell color
  showTextCellColor(chartFieldPropsSchema.definedSchema, containerName);

  // show alternative text
  showAternativeText(chartFieldPropsSchema.definedSchema, containerName);

  // add config to [subReport]
  addSubReportConfig(containerName);

  return chartFieldPropsSchema;

  /**
   * Disable|Enable sub report based on its container
   */
  function addSubReportConfig(containerName) {
    let mappingSource = chartFieldPropsSchema.mappingSource;

    mappingSource.isHiddenEmbeddedJavascript = isHiddenFunctionByPermissionKey(
      PERMISSION_KEY.ReportsFieldPropertiesEmbeddedJavaScript
    );
    mappingSource.isHiddenCustomURL = isHiddenFunctionByPermissionKey(PERMISSION_KEY.ReportsFieldPropertiesCustomURL);
    mappingSource.isHiddenSubReport = isHiddenFunctionByPermissionKey(PERMISSION_KEY.ReportsFieldPropertiesSubreport);

    function isHiddenFunctionByPermissionKey(permissionKey) {
      const showHidefunction = () => {
        const privilege = itemProps.authorization && itemProps.authorization.checkPrivileges([permissionKey]);
        if (!privilege) {
          return true;
        }
        return isShow();
      };
      return showHidefunction;
    }

    function isShow() {
      const chartType = reportPartContent.chartType;
      let isShowed = true;
      switch (chartType) {
        case CHART_TYPE.Line:
        case CHART_TYPE.Column:
        case CHART_TYPE.Bar:
        case CHART_TYPE.Area:
        case CHART_TYPE.Combination:
        case CHART_TYPE.Waterfall:
          isShowed = inContainer([ContainerName.LABELS]);
          break;
        case CHART_TYPE.Pie:
        case CHART_TYPE.Funnel:
        case CHART_TYPE.Donut:
        case CHART_TYPE.TreeMap:
          isShowed = inContainer([ContainerName.LABELS, ContainerName.SEPARATORS]);
          break;
        case CHART_TYPE.HeatMap:
          isShowed = inContainer([ContainerName.LABELS, ContainerName.VALUES]);
          break;
        case CHART_TYPE.Bubble:
        case CHART_TYPE.Scatter:
          isShowed = inContainer([ContainerName.VALUES_LABELS]);
          break;
        case CHART_TYPE.Sparkline:
          isShowed = inContainer([ContainerName.SEPARATORS]);
          break;
      }
      return !isShowed;
    }

    function inContainer(containers) {
      return containers.indexOf(containerName) > -1;
    }
  }

  function showAternativeText(definedSchema, containerName) {
    let isShowAlternativeText;

    switch (reportPartContent.chartType) {
      case CHART_TYPE.Sparkline:
        isShowAlternativeText = [ContainerName.LABELS, ContainerName.VALUES].indexOf(containerName) > -1 ? false : true;
        break;
      default:
        isShowAlternativeText = true;
    }

    if (!isShowAlternativeText) {
      delete definedSchema.fields.dataFormattings.fields.alternativeText;
    }
  }

  function showTextCellColor(definedSchema, containerName) {
    const colorDef = get('fields.dataFormattings.fields.color', definedSchema);

    if (!colorDef) {
      return;
    }

    let isShowCellColor;

    switch (reportPartContent.chartType) {
      case CHART_TYPE.Pie:
      case CHART_TYPE.Donut:
      case CHART_TYPE.Funnel:
      case CHART_TYPE.TreeMap:
        isShowCellColor = !(containerName === ContainerName.SEPARATORS);
        break;
      default:
        isShowCellColor = true;
    }

    if (!isShowCellColor) {
      return delete definedSchema.fields.dataFormattings.fields.color;
    }

    $izenda.extend(true, colorDef, {
      staticProps: {
        showTextColor: false,
        showCellColor: isShowCellColor
      }
    });
  }
}



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Fields/ReportBody/ReportPartType/ReportPartChart/Izenda.ChartFieldPropsSchema.js